import React, { useState } from 'react';
import { useOutlet } from '../lib/OutletContext';
import { useAuth } from '../lib/AuthContext';
import { Building2, ChevronRight, Loader2 } from 'lucide-react';

const OutletSelector = () => {
  const { outlets, selectOutlet, loading } = useOutlet();
  const { user } = useAuth();
  const [customOutletId, setCustomOutletId] = useState('');

  const handleSelectOutlet = (outletId) => {
    console.log('[OutletSelector] Selected outlet:', outletId);
    selectOutlet(outletId);
  };

  const handleCustomOutlet = (e) => {
    e.preventDefault();
    if (customOutletId.trim()) {
      handleSelectOutlet(customOutletId.trim());
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-zinc-400">Loading outlets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500/20 to-emerald-600/20 rounded-2xl mb-4">
          <Building2 className="w-8 h-8 text-emerald-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Select an Outlet</h1>
        <p className="text-zinc-400">
          {user?.is_super_admin 
            ? "As a Super Admin, you have access to all outlets. Please select one to continue."
            : "Please select an outlet to view inventory data."}
        </p>
      </div>

      {outlets.length > 0 ? (
        <div className="space-y-3">
          {outlets.map((outlet) => (
            <button
              key={outlet.outlet_id || outlet.id}
              onClick={() => handleSelectOutlet(outlet.outlet_id || outlet.id)}
              className="w-full flex items-center justify-between p-4 bg-[#18181b] border border-[#27272a] rounded-lg hover:border-emerald-500/50 hover:bg-[#1f1f23] transition-all group"
            >
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-zinc-400 group-hover:text-emerald-500" />
                <div className="text-left">
                  <p className="font-medium">{outlet.name || outlet.outlet_name || outlet.outlet_id || outlet.id}</p>
                  {outlet.address && (
                    <p className="text-sm text-zinc-500">{outlet.address}</p>
                  )}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-zinc-500 group-hover:text-emerald-500" />
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-[#18181b] border border-[#27272a] rounded-lg">
          <p className="text-zinc-400 mb-4">No outlets found in the system.</p>
          {user?.is_super_admin && (
            <p className="text-sm text-zinc-500">
              You can enter an outlet ID manually below.
            </p>
          )}
        </div>
      )}

      {/* Manual outlet entry for super admin */}
      {user?.is_super_admin && (
        <div className="mt-6 pt-6 border-t border-[#27272a]">
          <p className="text-sm text-zinc-400 mb-3">Or enter an outlet ID manually:</p>
          <form onSubmit={handleCustomOutlet} className="flex gap-2">
            <input
              type="text"
              value={customOutletId}
              onChange={(e) => setCustomOutletId(e.target.value)}
              placeholder="Enter outlet ID..."
              className="flex-1 px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-colors"
            />
            <button
              type="submit"
              disabled={!customOutletId.trim()}
              className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Select
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default OutletSelector;
